import { useEffect, useId, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { buildDrawerAskAIContextMessage } from '../utils/drawerAskAIContext'

interface UseDrawerAskAIContextArgs {
  isOpen: boolean
  contextLabel?: string
  contextDetails?: string
}

export function useDrawerAskAIContext({
  isOpen,
  contextLabel,
  contextDetails,
}: UseDrawerAskAIContextArgs) {
  const intl = useIntl()
  const eventBus = useTripPlannerEventBus()
  const reactId = useId()
  const source = useMemo(() => `drawer-ask-ai-${reactId}`, [reactId])

  const placeholder = intl.formatMessage(
    {
      id: 'johNJoOJenxVY0dTszC9',
      defaultMessage: 'Ask me anything about {contextLabel}',
      description: 'Placeholder text for AI assistant input field with context about current item',
    },
    { contextLabel: contextLabel ?? '' },
  )

  const context = useMemo(() => {
    if (!contextLabel) return undefined
    return buildDrawerAskAIContextMessage({ contextLabel, contextDetails })
  }, [contextLabel, contextDetails])
  const latestActivationValues = useRef({ context, placeholder })
  latestActivationValues.current = { context, placeholder }

  // Activate once per drawer source/label/open lifecycle. The main composer
  // intentionally ignores same-source refreshes so it captures what was on
  // screen when the drawer Ask AI context first became active.
  useEffect(() => {
    const latestContext = latestActivationValues.current.context
    if (!isOpen || !contextLabel || !latestContext) return

    eventBus.dispatch({
      type: 'activate_drawer_ask_ai_context',
      source,
      label: contextLabel,
      context: latestContext,
      placeholder: latestActivationValues.current.placeholder,
    })
  }, [contextLabel, eventBus, isOpen, source])

  useEffect(() => {
    if (!isOpen) return
    return () => {
      eventBus.dispatch({
        type: 'clear_drawer_ask_ai_context',
        source,
      })
    }
  }, [eventBus, isOpen, source])
}
